import pt from '../locales/pt';
import { paths } from '../routes/paths';
import { ALL_NAV_ITEMS } from '../components/layout/navItems';

function normalizePath(pathname) {
  if (!pathname) return '';
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed || paths.dashboard;
}

/**
 * @typedef {{ mode: 'logo' } | { mode: 'tab', title: string, actionTo?: string, actionLabel?: string } | { mode: 'nav', title: string, backTo: string }} MobileRouteHeaderConfig
 */

/** Rotas tab-root da bottom nav — título à esquerda, acção opcional à direita. */
const TAB_ROOT_ROUTES = [
  {
    path: paths.expenses,
    title: pt.expenses.title,
    actionTo: paths.categories,
    actionLabel: pt.expenses.hero.manageCategories,
  },
  {
    path: paths.income,
    title: pt.nav.income,
    actionTo: paths.incomeSources,
    actionLabel: pt.income.cta.manageSources,
  },
  {
    path: paths.patrimonio,
    title: pt.patrimonio.title,
    actionTo: paths.selfies,
    actionLabel: pt.patrimonio.viewReports,
    actionIcon: 'reports',
  },
];

/** Rotas exactas — ordenadas da mais específica para a mais genérica. */
const EXACT_ROUTE_META = [
  [paths.expensesNew, { title: pt.quickExpense.title, backTo: paths.expenses }],
  [paths.goalsNew, { title: pt.goals.wizard.title, backTo: paths.goals }],
  [paths.emergencyFundWizard, { title: pt.emergencyFund.wizard.title, backTo: paths.emergencyFund }],
  [
    paths.emergencyFundPlano,
    { title: pt.emergencyFund.dashboard.advanced.title, backTo: paths.emergencyFund },
  ],
  [paths.dividasPlan, { title: pt.liabilities.repaymentWizard.title, backTo: paths.dividas }],
  [paths.dividasEvaluator, { title: pt.liabilities.evaluator.title, backTo: paths.dividas }],
  [paths.selfieNew, { title: pt.snapshots.history.title, backTo: paths.selfies }],
  [paths.incomeSources, { title: pt.nav.incomeSources, backTo: paths.income }],
  [paths.settingsProfile, { title: pt.settings.profile.title, backTo: paths.settings }],
  [paths.settingsAccount, { title: pt.settings.account.title, backTo: paths.settings }],
  [paths.settingsBudgetMethod, { title: pt.settings.budgetMethod.navTitle, backTo: paths.settings }],
  [paths.settingsCycleDay, { title: pt.settings.cycleDay.navTitle, backTo: paths.settings }],
  [paths.settingsHelp, { title: pt.settings.help.title, backTo: paths.settings }],
  [paths.settingsDeleteAccount, { title: pt.settings.deleteAccount.title, backTo: paths.settings }],
  [paths.settingsNotifications, { title: pt.settings.title, backTo: paths.settings }],
  [paths.settingsReminders, { title: pt.settings.title, backTo: paths.settings }],
  [paths.settingsAppearance, { title: pt.settings.appearance.title, backTo: paths.settings }],
  [paths.categories, { title: pt.categories.title, backTo: paths.dashboard }],
  [paths.budget, { title: pt.budget.title, backTo: paths.dashboard }],
  [paths.goals, { title: pt.goals.title, backTo: paths.dashboard }],
  [paths.pots, { title: pt.pots.title, backTo: paths.dashboard }],
  [paths.calendar, { title: pt.calendar.title, backTo: paths.dashboard }],
  [paths.emergencyFund, { title: pt.emergencyFund.title, backTo: paths.dashboard }],
  [paths.ativos, { title: pt.accounts.title, backTo: paths.patrimonio }],
  [paths.dividas, { title: pt.liabilities.title, backTo: paths.dashboard }],
  [paths.selfies, { title: pt.snapshots.history.title, backTo: paths.dashboard }],
  [paths.cycleSettings, { title: pt.cycleSettings.title, backTo: paths.dashboard }],
  [paths.settings, { title: pt.settings.title, backTo: paths.dashboard }],
].sort((a, b) => b[0].length - a[0].length);

/** Rotas com parâmetros — avaliadas por ordem de especificidade. */
const PATTERN_ROUTE_META = [
  {
    pattern: /^\/app\/selfie\/result\/([^/]+)\/report$/,
    title: () => pt.snapshots.report.mobileTitle,
    backTo: () => paths.selfies,
  },
  {
    pattern: /^\/app\/selfie\/result\/([^/]+)$/,
    title: () => pt.snapshots.result.title,
    backTo: () => paths.selfies,
  },
  {
    pattern: /^\/app\/selfie\/([^/]+)\/edit$/,
    title: () => pt.snapshots.edit.title.replace(' de {month}', ''),
    backTo: () => paths.selfies,
  },
  {
    pattern: /^\/app\/(?:dividas|motion)\/simulador\/([^/]+)$/,
    title: () => pt.liabilities.simulator.title,
    backTo: () => paths.dividas,
  },
  {
    pattern: /^\/app\/goals\/([^/]+)$/,
    title: () => pt.goals.title,
    backTo: () => paths.goals,
  },
  {
    pattern: /^\/app\/categories\/([^/]+)$/,
    title: () => pt.categories.title,
    backTo: () => paths.categories,
  },
];

/**
 * Título de página para topbar desktop / tablet.
 * @param {string} pathname
 * @returns {string}
 */
export function getAppPageTitle(pathname) {
  const config = getMobileRouteHeader(pathname);
  if (config.mode === 'logo') return pt.nav.dashboard;
  return config.title || pt.nav.dashboard;
}

/**
 * Resolve o header mobile (logo vs navegação com voltar).
 * @param {string} pathname
 * @returns {MobileRouteHeaderConfig}
 */
export function getMobileRouteHeader(pathname) {
  const normalized = normalizePath(pathname);

  if (normalized === paths.dashboard) {
    return { mode: 'logo' };
  }

  const tabRoot = TAB_ROOT_ROUTES.find((route) => normalized === route.path);
  if (tabRoot) {
    return {
      mode: 'tab',
      title: tabRoot.title,
      actionTo: tabRoot.actionTo,
      actionLabel: tabRoot.actionLabel,
      actionIcon: tabRoot.actionIcon,
    };
  }

  for (const [routePath, meta] of EXACT_ROUTE_META) {
    if (normalized === routePath) {
      return { mode: 'nav', title: meta.title, backTo: meta.backTo };
    }
  }

  for (const rule of PATTERN_ROUTE_META) {
    const match = normalized.match(rule.pattern);
    if (match) {
      return {
        mode: 'nav',
        title: rule.title(normalized, match),
        backTo: rule.backTo(normalized, match),
      };
    }
  }

  const navMatch = [...ALL_NAV_ITEMS]
    .sort((a, b) => b.to.length - a.to.length)
    .find((item) => normalized === item.to || normalized.startsWith(`${item.to}/`));

  if (navMatch) {
    return {
      mode: 'nav',
      title: navMatch.label,
      backTo: normalized === navMatch.to ? paths.dashboard : navMatch.to,
    };
  }

  return {
    mode: 'nav',
    title: pt.nav.dashboard,
    backTo: paths.dashboard,
  };
}
